import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CheckCircle2,
  Clock3,
  Clock6,
  Clock9,
  Clock12,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { cn } from "@/utils/stylesUtils";
import { useAudioPreference } from "@/hooks/useAudioPreference";
import { useSettingsState } from "@/screens/OptionsScreen/hooks/useSettingsState";
import { PRACTICE_SETTINGS_ROUTE_CONFIG } from "./config";

const TIMER_OPTIONS = [
  { label: "10 seconds", value: "10 seconds", Icon: Clock3 },
  { label: "20 seconds", value: "20 seconds", Icon: Clock6 },
  { label: "30 seconds", value: "30 seconds", Icon: Clock9 },
  { label: "1 minute", value: "1 minute", Icon: Clock12 },
  { label: "2 minutes", value: "2 minutes", Icon: Clock12, isDouble: true },
];

const VOICE_OPTIONS = [
  { label: "Lina (English)", value: "english female" },
  { label: "Drew (English)", value: "english male" },
  { label: "Lina (Spanish)", value: "spanish female" },
];

function SectionCard({ children }) {
  return (
    <div className="rounded border border-[#DDDDDD] bg-white p-5 shadow-sm">
      {children}
    </div>
  );
}

function OptionButton({ selected, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 rounded border px-3 py-2 text-left transition",
        selected
          ? "border-[#3286DA] bg-[#3386D912]"
          : "border-[#DDDDDD] bg-white hover:bg-gray-50",
      )}
    >
      {children}
    </button>
  );
}

function SectionHeading({ title, description }) {
  return (
    <div className="mb-4">
      <div className="text-base font-bold text-[#1E2D3D]">{title}</div>
      <div className="mt-1 text-sm text-[#555555BF]">{description}</div>
    </div>
  );
}

export default function PracticeSettingsOverlay({ isOpen, onClose }) {
  const { pathname } = useLocation();
  const routeConfig = PRACTICE_SETTINGS_ROUTE_CONFIG[pathname];
  const [playing, setPlaying] = useAudioPreference();
  const settings = useSettingsState();
  const [feedbackMessage, setFeedbackMessage] = useState("");

  useEffect(() => {
    if (!isOpen || !routeConfig) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose, routeConfig]);

  useEffect(() => {
    if (!feedbackMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setFeedbackMessage("");
    }, 15000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [feedbackMessage]);

  useEffect(() => {
    if (!isOpen) {
      setFeedbackMessage("");
    }
  }, [isOpen]);

  if (!isOpen || !routeConfig) {
    return null;
  }

  const showSettingsSavedToast = (description) => {
    setFeedbackMessage(description);
  };

  const saveSoundSetting = (nextPlaying) => {
    if (playing === nextPlaying) {
      return;
    }

    document.cookie = `audio=${nextPlaying}; path=/; SameSite=Strict; Secure`;
    setPlaying(nextPlaying);
    showSettingsSavedToast(
      `Sound has been turned ${nextPlaying ? "on" : "off"}`,
    );
  };

  const saveUserSetting = async ({
    field,
    value,
    setLocal,
    successMessage,
    currentValue,
  }) => {
    if (currentValue === value) {
      return;
    }

    const didSave = await settings.handleSettingChange(field, value, setLocal);

    if (didSave) {
      showSettingsSavedToast(successMessage);
    }
  };

  const renderSection = (section) => {
    if (section === "sound") {
      return (
        <SectionCard key={section}>
          <SectionHeading
            title="Sound"
            description="Turn wellness practice audio on or off."
          />
          <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
            <OptionButton
              selected={playing}
              onClick={() => saveSoundSetting(true)}
            >
              <Volume2 className="h-4 w-4" />
              <span>On</span>
            </OptionButton>
            <OptionButton
              selected={!playing}
              onClick={() => saveSoundSetting(false)}
            >
              <VolumeX className="h-4 w-4" />
              <span>Off</span>
            </OptionButton>
          </div>
        </SectionCard>
      );
    }

    if (section === "voice") {
      return (
        <SectionCard key={section}>
          <SectionHeading
            title="Voice"
            description="Choose the voice that guides your practice."
          />
          <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
            {VOICE_OPTIONS.map(({ label, value }) => (
              <OptionButton
                key={value}
                selected={settings.voice === value}
                onClick={() =>
                  saveUserSetting({
                    field: "voice",
                    value,
                    setLocal: settings.setVoice,
                    currentValue: settings.voice,
                    successMessage: `Voice has been changed to ${label}`,
                  })
                }
              >
                <span>{label}</span>
              </OptionButton>
            ))}
          </div>
        </SectionCard>
      );
    }

    if (section === "timer") {
      return (
        <SectionCard key={section}>
          <SectionHeading
            title="Wellness Timer (Step 3)"
            description="Choose how long you will practice in Step 3."
          />
          <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
            {TIMER_OPTIONS.map(({ label, value, Icon, isDouble }) => (
              <OptionButton
                key={value}
                selected={settings.wellnessTimer === value}
                onClick={() =>
                  saveUserSetting({
                    field: "wellnesstimer",
                    value,
                    setLocal: settings.setWellnessTimer,
                    currentValue: settings.wellnessTimer,
                    successMessage: `Timer has been set to ${label}`,
                  })
                }
              >
                <div className="flex items-center gap-1">
                  {isDouble ? (
                    <>
                      <Icon className="h-4 w-4" />
                      <Icon className="h-4 w-4" />
                    </>
                  ) : (
                    <Icon className="h-4 w-4" />
                  )}
                </div>
                <span>{label}</span>
              </OptionButton>
            ))}
          </div>
        </SectionCard>
      );
    }

    // if (section === "emotions") {
    //   return (
    //     <SectionCard key={section}>
    //       <SectionHeading
    //         title="Emotions"
    //         description="More emotion settings will be added here soon."
    //       />
    //       <div className="rounded border border-dashed border-[#DDDDDD] bg-[#F8FAFC] px-4 py-3 text-sm text-[#555555BF]">
    //         To be added
    //       </div>
    //     </SectionCard>
    //   );
    // }

    return null;
  };

  return (
    <div className="fixed inset-0 z-[1100] sm:left-[130px]" onClick={onClose} role="presentation">
      <div className="absolute inset-0 bg-[#1E2D3D]/25" />
      <div
        className="absolute inset-y-0 right-0 h-full w-[90%] overflow-y-auto bg-[#F8FAFC] shadow-[0_20px_60px_rgba(18,45,70,0.22)] sm:w-[60%]"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="practice-settings-heading"
      >
        {feedbackMessage ? (
          <div className="pointer-events-none absolute right-6 top-20 z-20 max-w-[360px]">
            <div className="rounded border border-green-200 bg-green-50 px-4 py-3 shadow-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-600" />
                <div className="min-w-0">
                  <div className="font-semibold text-green-900">
                    Settings Saved
                  </div>
                  <div className="text-sm text-green-800">
                    {feedbackMessage}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#E5E7EB] bg-white px-6 py-5">
          <h2
            id="practice-settings-heading"
            className="text-2xl font-bold text-[#1E2D3D]"
          >
            Practice Settings
          </h2>
          <button
            type="button"
            className="rounded-full p-2 text-[#1E2D3D] transition hover:bg-gray-100"
            onClick={onClose}
            aria-label="Close practice settings"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-6">
          {routeConfig.sections.map(renderSection)}
        </div>
      </div>
    </div>
  );
}
